import React from "react";
import { Elements } from "@stripe/react-stripe-js";
import YouPayLine from "./YouPayLine";
import InlinePayOptions from "./InlinePayOptions";
import SetlistSealedArt from "./SetlistSealedArt";
import { shopPriceLabel } from "./FanShopSheet";
import { SpotifyMark, AppleMark } from "../pages/PlaylistPage";
import { currencySymbol, formatAmountForCopy, formatSingleAmount, formatTxnAmount } from "../utils/currency";
import { SUFFIX_CUR } from "../utils/requestsFormat";
import { sanitizeMoney, stripePromise } from "../lib/fanCheckout";
import type { BuyCheckout as BuyCheckoutState } from "../hooks/useBuyCheckout";
import type { SongRequest } from "../hooks/useSongRequest";
import { t } from "../i18n";
import { T } from "../i18n/T";

// Buying a playlist on the fan page: the entry banner ("DJ Setlist: $X" or
// "N playlists: from $X" with a stocked shelf) and the checkout section
// (the product pictured, the bid box for tonight's set, the fixed price for
// a shelf item, the CTA, then the payment reveal). Split out of
// pages/RequestPage.tsx; the state lives in hooks/useBuyCheckout.

type BannerProps = { displayDj: any; isProfilePreview: boolean; buy: BuyCheckoutState; tipMode: boolean; song: SongRequest };

export function PlaylistBanner({ displayDj, isProfilePreview, buy, tipMode, song }: BannerProps) {
  const { setlistOn, hasShelf, buyMode, setShopSheetOpen, openBuyMode, setlistChipPrice, shopPlaylists, shelfFromPrice } = buy;
  const { selectedTrack } = song;
  return (
    <>
      {/* Playlist banner (founder FINAL pick C — saw B live,
          preferred C): parallel words, own shapes. A centered button
          above the song section — not edge-to-edge, price a step
          larger than the label. Search stays instantly visible. */}
      {/* Hidden once a song is picked (checkout focus, founder) —
          returns when the fan deselects and goes back. */}
      {/* Storefront v2 (founder pick 5C): with a stocked shelf the
          banner counts the products and opens the sheet; with an empty one
          it stays EXACTLY today's straight-to-checkout button, so nothing
          changes for a DJ who never fills a slot. */}
      {(setlistOn || hasShelf) && !tipMode && !buyMode && !selectedTrack && (
        <button
          type="button"
          className="rq-pl-banner"
          disabled={isProfilePreview}
          title={isProfilePreview ? t("buyCheckout.availableOnYourLive") : undefined}
          onClick={() => (hasShelf ? setShopSheetOpen(true) : openBuyMode())}
          aria-label={
            hasShelf
              ? t("buyCheckout.seeNamesPlaylists", { name: String(displayDj.name) })
              : t("buyCheckout.buyThePlaylist", { amount: shopPriceLabel(setlistChipPrice, displayDj.currency) })
          }
        >
          <span className="rq-pl-banner-marks" aria-hidden>
            <span className="rq-pl-mark rq-pl-mark--spotify"><SpotifyMark /></span>
            <span className="rq-pl-mark rq-pl-mark--apple"><AppleMark /></span>
          </span>
          <span className="rq-pl-banner-label">
            {hasShelf
              ? t("buyCheckout.playlistsCount", { count: shopPlaylists.length + (setlistOn ? 1 : 0) })
              : t("buyCheckout.djSetlist")}
          </span>
          <span className="rq-pl-banner-price">
            {hasShelf
              ? shelfFromPrice === 0
                ? t("buyCheckout.freeUp")
                : t("buyCheckout.from", { amount: shopPriceLabel(shelfFromPrice, displayDj.currency) })
              : shopPriceLabel(setlistChipPrice, displayDj.currency)}
          </span>
          <span className="rq-pl-banner-chev" aria-hidden>›</span>
        </button>
      )}
    </>
  );
}

type Props = {
  displayDj: any;
  isProfilePreview: boolean;
  avatarImgSrc: (u: string) => string;
  emailForLink: string;
  setEmailForLink: (v: string) => void;
  buy: BuyCheckoutState;
};

export default function BuyCheckout({ displayDj, isProfilePreview, avatarImgSrc, emailForLink, setEmailForLink, buy }: Props) {
  const {
    buyMode,
    buyAmount,
    setBuyAmount,
    buyBidInfoOpen,
    setBuyBidInfoOpen,
    buyBidFocused,
    setBuyBidFocused,
    buyBidPlaceholder,
    setBuyBidPlaceholder,
    buyOrderId,
    buyClientSecret,
    buyShowPay,
    buyShowCard,
    setBuyShowCard,
    buyProcessing,
    setBuyProcessing,
    buyPayError,
    setBuyPayError,
    buyCtaTapped,
    freeBusy,
    setlistChipPrice,
    buyShopItem,
    buyProductPrice,
    buyIsFree,
    buyTotal,
    buySucceeded,
    claimFreePlaylist,
    startBuyPurchase,
  } = buy;
  return (
    <>
      {/* ── Buy Playlist (Setlist sale) — v4 FAN 02. Amount pre-filled at
          the DJ's price, NumberPad entry, bids only go UP; the card is
          authorized now and charged only when the playlist is delivered. */}
      {buyMode && !isProfilePreview && (
        <section className="rq-tip-section rq-buy-section">
          <div className="rq-bid-card">
            {/* Fun-first redesign (founder, tip-screen style): flying
                money up top, emoji amount tiles that get happier as the
                support grows, Custom kept below. */}
            {/* The product, pictured (founder: "hard to see what
                they're actually purchasing"). Tonight's set = the sealed
                record + ribbon — the unopened-gift art from the pending
                page, now shown BEFORE purchase too. A shelf playlist shows
                its real cover mosaic instead: it already exists, so the
                honest picture is the covers. The two service marks ride
                the bottom-right corner of either one. */}
            <div className="rq-buy-art" aria-hidden>
              {buyShopItem ? (
                <div className="rq-buy-art-mosaic">
                  {(buyShopItem.covers.length > 0
                    ? buyShopItem.covers.slice(0, 4)
                    : [null]
                  ).map((src, i) =>
                    src ? (
                      <img key={i} src={src} alt="" />
                    ) : (
                      <span key={i} className="rq-buy-art-blank" />
                    )
                  )}
                </div>
              ) : (
                <SetlistSealedArt
                  djName={displayDj.name}
                  djAvatarUrl={displayDj.avatarUrl ? avatarImgSrc(displayDj.avatarUrl) : null}
                />
              )}
              <span className="rq-buy-art-marks">
                <span className="rq-pl-mark rq-pl-mark--spotify"><SpotifyMark /></span>
                <span className="rq-pl-mark rq-pl-mark--apple"><AppleMark /></span>
              </span>
            </div>
            <div className="rq-bid-title">
              {buyShopItem
                ? buyShopItem.name
                : t("buyCheckout.requestSetlist", { amount: shopPriceLabel(setlistChipPrice, displayDj.currency) })}
            </div>
            {/* A shelf item has ONE price (founder Q4) — no tiles, no
                Custom. Generosity stays on tonight's set and on tips. */}
            {buyShopItem && (
              <div className="rq-buy-fixed">
                <span className="rq-buy-fixed-amt">{shopPriceLabel(buyProductPrice, displayDj.currency)}</span>
                <span className="rq-buy-fixed-sub">
                  {t("buyCheckout.tracksYoursRightAway", { count: buyShopItem.trackCount })}
                </span>
              </div>
            )}
            {/* PLACE YOUR BID — the song box's exact mechanism on tonight's
                set (founder 8/29): min-locked input, faint price placeholder,
                blur clamps low bids back to the setlist price. A FREE set
                shows no bid box at all (one-tap add, nothing to bid on —
                the old tiles self-hid at price 0, this must too). */}
            {!buyShopItem && !buyIsFree && (
              <div className="rq-buy-bid">
                <div className="rq-bid-header">
                  <div className="rq-bid-title">{t("buyCheckout.placeYourBid")}</div>
                  <button
                    type="button"
                    className="rq-tier-info-btn rq-bid-info-btn"
                    onClick={() => {
                      setBuyBidInfoOpen((v) => !v);
                    }}
                    aria-label={t("buyCheckout.whatYouLlPay")}
                    aria-expanded={buyBidInfoOpen}
                  >
                    i
                  </button>
                  {buyBidInfoOpen && (
                    <div className="rq-tier-info-popover rq-bid-info-popover" role="status">
                      <div className="rq-bid-info-row">
                        <T k="buyCheckout.setlistPriceIs" vars={{ amount: formatAmountForCopy(setlistChipPrice, displayDj.currency), name: displayDj.name ?? "" }} tags={{ 0: (c) => <strong>{c}</strong> }} />
                      </div>
                      <div className="rq-bid-info-row">
                        <span className="rq-bid-info-fast">{t("buyCheckout.higherBidsGetPriority")}</span>
                      </div>
                      <div className="rq-bid-info-fee">
                        {t("buyCheckout.aSmallFeeIs")}
                      </div>
                    </div>
                  )}
                </div>
                <div className="rq-bid-sub">{t("buyCheckout.higherBidsGetMore")}</div>
                {/* The whole pill is the tap target, as on the song checkout. */}
                <label className="rq-bid-input-wrap">
                  {!SUFFIX_CUR.has((displayDj.currency || "usd").toLowerCase()) && (
                    <span className="rq-bid-cur">{currencySymbol(displayDj.currency)}</span>
                  )}
                  <input
                    type="text"
                    inputMode="decimal"
                    enterKeyHint="done"
                    autoComplete="off"
                    className="rq-bid-input"
                    value={buyAmount}
                    onFocus={() => {
                      setBuyBidFocused(true);
                      if (buyAmount && buyAmount.trim() !== "") {
                        setBuyBidPlaceholder(buyAmount);
                      }
                      setBuyAmount("");
                    }}
                    onBlur={(e) => {
                      setBuyBidFocused(false);
                      const v = Number(e.target.value);
                      if (!v) {
                        setBuyAmount(buyBidPlaceholder || String(setlistChipPrice));
                      } else if (v < setlistChipPrice) {
                        setBuyAmount(String(setlistChipPrice));
                        setBuyBidPlaceholder(String(setlistChipPrice));
                      } else {
                        setBuyBidPlaceholder(String(v));
                      }
                    }}
                    onChange={(e) => setBuyAmount(sanitizeMoney(e.target.value))}
                    placeholder={buyBidPlaceholder || String(setlistChipPrice)}
                  />
                  {SUFFIX_CUR.has((displayDj.currency || "usd").toLowerCase()) && (
                    <span className="rq-bid-cur">{currencySymbol(displayDj.currency)}</span>
                  )}
                </label>
                {buyBidFocused && buyAmount.trim() !== "" && Number(buyAmount) > 0 && Number(buyAmount) < setlistChipPrice && (
                  <div className="rq-bid-error" role="alert">
                    <T k="buyCheckout.minimumBidIs" vars={{ amount: formatAmountForCopy(setlistChipPrice, displayDj.currency) }} tags={{ 0: (c) => <strong>{c}</strong> }} />
                  </div>
                )}
              </div>
            )}
            {/* The money truth in Custom's old slot (founder,
                round 6): fee-inclusive total + the supporting line, always
                visible BEFORE any tap, live with the selected tile. A free
                playlist has neither — there's no charge to explain. */}
            {!buyIsFree && (
              <>
                <YouPayLine
                  variant="panel"
                  className="rq-you-pay--incard"
                  totalLabel={formatSingleAmount(buyTotal, displayDj.currency)}
                />
                <div className="rq-gen-line">
                  {t("buyCheckout.youReSupportingDirectly", { name: displayDj.name ?? "" })}
                </div>
              </>
            )}
            {/* ONE purchase button (founder pick A, platform-terms
                review — replaces the two earlier service buttons). The price
                names the DJ's SETLIST, never a streaming service: Spotify and
                Apple Music are how it is delivered, not what is sold. Selling
                "a Spotify playlist" is what Spotify's rules forbid (Developer
                Terms V.5; User Guidelines), and Apple's MusicKit terms bar
                monetizing access to Apple Music — neither describes this sale,
                and the wording must not suggest it does.

                The service marks moved to the delivery line below, where they
                say how it arrives. Fans lose nothing: both buttons always did
                the identical purchase, and the delivered page still offers
                both — so one button is also the drunk-proof read.

                A FREE playlist takes no card, no wallet, no Stripe — one tap
                posts and the playlist opens. */}
            <button
              type="button"
              className="rq-buy-cta"
              onClick={buyIsFree ? claimFreePlaylist : startBuyPurchase}
              disabled={freeBusy}
            >
              {buyIsFree
                ? freeBusy ? t("buyCheckout.oneMoment") : t("buyCheckout.getTheSetlistFree")
                : t("buyCheckout.getTheSetlist", { amount: formatTxnAmount(buyTotal, displayDj.currency) })}
            </button>
            <div className="rq-buy-delivery">
              <span className="rq-pl-mark rq-pl-mark--spotify"><SpotifyMark /></span>
              <span className="rq-pl-mark rq-pl-mark--apple"><AppleMark /></span>
              {t("buyCheckout.landsInYourSpotify")}
            </div>
            {/* Trust copy removed (founder: "more simple") — the
                charge-at-delivery promise still reaches the fan on the
                pending page, the delivery pill, and both emails. */}
            {/* Terms consent on the page BEFORE the wallet ever opens
                (founder + legal round, build 34) — the pay box below
                renders no text of its own for playlists. */}
            {!buyIsFree && (
              <div className="rq-pay-disclosure">
                <T k="buyCheckout.byPayingYouAgree" tags={{ 0: (c) => <a href="/terms" target="_blank" rel="noopener noreferrer">{c}</a> }} />
              </div>
            )}
          </div>

          {buyCtaTapped && !buyClientSecret && !buyPayError && (
            <div
              className="rq-inline-pay rq-inline-pay--inpaybox rq-pay-loading-placeholders"
              aria-busy="true"
              aria-label={t("buyCheckout.loadingPaymentOptions")}
            >
              <div className="rq-inline-pay">
                <div className="rq-wallets">
                  <div className="rq-express-vertical">
                    <div className="rq-fake-express-slot">
                      <span className="rq-fake-express-slot__label">{t("buyCheckout.loadingPayment")}</span>
                    </div>
                  </div>
                  <div className="rq-add-card" aria-hidden="true">
                    {t("buyCheckout.addCardDetails")}
                  </div>
                </div>
              </div>
            </div>
          )}

          {buyCtaTapped && buyShowPay && stripePromise && buyClientSecret && (
            <Elements
              key={`buy-el-${buyClientSecret}-${buyTotal.toFixed(2)}`}
              stripe={stripePromise}
              options={{
                clientSecret: buyClientSecret,
                appearance: { theme: "night" },
                locale: "en",
              }}
            >
              <div className="rq-inline-pay rq-inline-pay--inpaybox">
                <InlinePayOptions
                  key={`buy-ipo-${buyClientSecret}-${buyTotal.toFixed(2)}`}
                  orderId={buyOrderId}
                  emailForLink={emailForLink}
                  setEmailForLink={setEmailForLink}
                  context={buyShopItem ? "shop" : "playlist"}
                  djName={displayDj.name}
                  hideWallets
                  showCardForm={buyShowCard}
                  setShowCardForm={setBuyShowCard}
                  processing={buyProcessing}
                  setProcessing={setBuyProcessing}
                  payError={buyPayError}
                  setPayError={setBuyPayError}
                  selectedTrack={null}
                  totalToCharge={buyTotal}
                  onPaymentSuccess={async (piId?: string) => {
                    if (!buyOrderId) return;
                    await buySucceeded(buyOrderId, piId);
                  }}
                />
              </div>
            </Elements>
          )}

          {buyPayError && !buyShowPay && (
            <div className="rq-pay-error">{buyPayError}</div>
          )}

        </section>
      )}
    </>
  );
}
